<template>
  <section class="section">
    <div class="section-container">
      <div class="section-wrapper">
        <header class="section-header text-center mb-5">
          <hgroup class="hgroup">
            <h1 class="headline-1">L’assistant ultime propulsé par l’IA.</h1>
            <p class="subheading text-center mb-0">
              Appels, e-mails, réseaux sociaux, traduction, contenus, WhatsApp :
              Man-IA regroupe tous vos outils IA dans une seule plateforme
              fluide et connectée.
            </p>
            <div class="note-wrapper mt-0">
              <small class="text-muted d-block"
                >Cliquez pour discuter avec Sun-i l'une de nos IA</small
              >
              <div class="triangle-down mx-auto mt-1"></div>
            </div>
          </hgroup>
        </header>

        <ul class="section-cards">
          <li class="card" v-for="(card, index) in cards" :key="index">
            <button @click="openChat" class="card-wrap">
              <figure class="figure">
                <div class="visual">
                  <img
                    class="img"
                    :src="card.img"
                    width="222"
                    height="175"
                    :alt="card.alt"
                  />
                </div>
                <div class="figcaption">
                  <strong>{{ card.title }}</strong
                  ><br />
                  {{ card.description }}
                </div>
              </figure>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Popover modale -->
    <div popover id="chat-popover" class="chat" ref="chatPopover">
      <div class="chat-content-wrapper">
        <header class="chat-header">
          <button
            @click="closeChat"
            class="chat-popover-close-btn"
            aria-label="Close Chat"
            title="Close Chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path
                d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"
              ></path>
            </svg>
          </button>
        </header>

        <section class="chat-section">
          <div class="chat-messages" ref="chatMessages">
            <div
              v-for="(msg, i) in iaConversation"
              :key="i"
              :class="['message', msg.role]"
            >
              <p class="message-content" v-html="msg.content"></p>
            </div>
            <!-- ✅ Loader IA en cours -->
            <div v-if="loading" ref="chatLoader" class="ia-loader-wrapper">
              <div class="dot-loader">
                <span></span><span></span><span></span>
              </div>
              <em class="ia-loader-text">Raisonnement en cours…</em>
            </div>
            <!-- Ancre de scroll invisible -->
            <div ref="scrollAnchor" style="height: 1px; margin-top: 32px"></div>
          </div>

          <form @submit.prevent="submitChat">
            <div class="chat-input-container">
              <textarea
                v-model="userInput"
                class="chat-textarea"
                placeholder="Posez vos questions"
                @keydown.enter.prevent="submitChat"
              ></textarea>
              <button
                type="submit"
                class="chat-submit-btn"
                aria-label="Chat Submit"
                title="Chat Submit"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5664 3.26059C2.32007 2.76794 2.51587 2.5663 3.02256 2.81964L21.0172 11.8169C21.5155 12.0661 21.5206 12.4896 21.0494 12.7513L3.69739 22.3914C3.21698 22.6582 2.98859 22.4559 3.18594 21.9427L6.36304 13.6822L16.2625 12.268L6.36306 10.8538L2.5664 3.26059Z"
                  />
                </svg>
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "ManiaChat",

  data() {
    return {
      userInput: "",
      cards: [],
      animatedResponse: "",
    };
  },

  watch: {
    iaConversation: {
      handler() {
        this.scrollToBottom();
      },
      deep: true, // ✅ observe les changements internes (contenu des messages)
    },
  },

  computed: {
    ...mapGetters("ia", [
      "iaLoading",
      "iaConversation",
      "iastreaming",
      "iaError",
    ]),
    loading() {
      return this.iaLoading && !this.iaStreaming;
    },
    result() {
      return this.iaResponse;
    },
  },

  methods: {
    ...mapActions("ia", ["sendIaMessage"]),

    openChat() {
      const el = this.$refs.chatPopover;
      if (el && el.showPopover) {
        el.showPopover();
      }
    },

    closeChat() {
      const el = this.$refs.chatPopover;
      if (el && el.hidePopover) {
        el.hidePopover();
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const anchor = this.$refs.scrollAnchor;
        if (anchor && anchor.scrollIntoView) {
          anchor.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      });
    },

    scrollToLoader() {
      this.$nextTick(() => {
        const loader = this.$refs.chatLoader;
        if (loader) {
          loader.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async submitChat() {
      const message = this.userInput.trim();

      if (!message) {
        console.warn("⚠️ Message vide");
        return;
      }

      this.userInput = "";
      try {
        this.scrollToLoader();
        await this.sendIaMessage(message);
      } catch (error) {
        console.error("❌ Erreur lors de l'envoi :", error);
        this.userInput = message; // Reinjecte en cas d'échec
      }
    },
  },

  mounted() {
    this.cards = [
      {
        img: require("@/assets/pictures/ai-card-1.jpg"),
        alt: "Assistant vocal",
        title: "Assistant vocal",
        description: "Répond aux appels et prend vos rendez-vous.",
      },
      {
        img: require("@/assets/pictures/ai-card-2.jpg"),
        alt: "Gestion e-mail",
        title: "Gestion e-mail",
        description: "Classe, répond et planifie pour vous.",
      },
      {
        img: require("@/assets/pictures/ai-card-3.jpg"),
        alt: "Réseaux sociaux",
        title: "Réseaux sociaux",
        description: "Crée et publie vos contenus selon votre stratégie.",
      },
      {
        img: require("@/assets/pictures/ai-card-4.png"),
        alt: "Et bien plus",
        title: "Et bien plus",
        description: "Traduction, images, WhatsApp, outils IA…",
      },
    ];
  },
};
</script>

<style scoped lang="scss">
@layer design-system, reset, base, utilities, components, layout, overrides;
@import url("https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400..700&display=swap");
@import "https://unpkg.com/open-props" layer(design-system);

@layer reset {
  *,
  ::before,
  ::after {
    box-sizing: border-box;
  }

  :where(:not(dialog)) {
    margin: 0;
  }

  :where(html) {
    -webkit-text-size-adjust: none;

    @media (prefers-reduced-motion: no-preference) {
      scroll-behavior: smooth;
    }
  }

  :where(body) {
    min-block-size: 100svb;
    -webkit-font-smoothing: antialiased;
  }
}

@layer base {
  html {
    --surface-1: white;
    --text-1: var(--stone-11);
    --primary: var(--indigo-7);
    --secondary-container: var(--gray-12);
    --font-family-default: "Source Sans 3", var(--font-neo-grotesque);

    /* Override the default Open Props shadow properties for dark mode. 
        Make sure to use @layer when importing Open Props. */

    @media (prefers-color-scheme: dark) {
      --shadow-color: 220 3% 15%;
      --shadow-strength: 1%;
    }
  }
}

@layer layout {
  .section {
    display: grid;
    font-family: var(--font-family-default);
    padding-block: var(--size-8);
    padding-inline: var(--size-7);
    place-items: start center;
    position: relative;
    margin-bottom: 160px;

    /* xl-n-below */
    @media (width < 1440px) {
      background-position: top 20% center;
      background-size: 150%;
    }

    /* md-n-below */
    @media (width < 768px) {
      background-position: top 20% center;
      background-size: 1000%;
    }
  }

  .section-container {
    inline-size: min(1028px, 100%);
  }

  .section-wrapper {
    display: grid;
    place-items: center;
    gap: var(--size-7);
  }

  .section-header {
    display: grid;
    place-items: center;
    gap: var(--size-5);
  }

  .logo {
    font-size: var(--font-size-4);
    font-weight: var(--font-weight-7);
  }

  .hgroup {
    display: grid;
    place-items: center;
    gap: var(--size-1);
    text-align: center;
  }

  .headline-1 {
    font-size: var(--font-size-5);
  }

  .subheading {
    font-size: var(--font-size-3);
  }

  .cta-group {
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;

    /* sm-n-above */
    @media (width >=560px) {
      grid-auto-flow: column;
      grid-template-columns: 1fr 1fr;
    }
  }

  .section-cards {
    display: grid;
    padding: 0 !important;
    list-style: none;
    gap: 32px;

    @media (width >=460px) {
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    /* md-n-above */
    @media (width >=768px) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }

  .card-wrap {
    text-decoration: none;
    color: $color-text;
    background: var(--card-bg);
    background-image: linear-gradient(60deg, #000000 0%, #292f35 100%);
    display: grid;
    block-size: 100%;
    border-radius: 12px;
    padding: 12px;
    border-width: var(--border-size-1);
    border-color: black;
    border-style: solid;
    text-align: start;
    cursor: pointer;
    inline-size: 100%;
    box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.75);
    -webkit-box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.75);
    -moz-box-shadow: 10px 10px 5px 0px rgba(0, 0, 0, 0.75);
  }

  .figure {
    display: grid;
    gap: var(--size-3);
    grid-template-rows: min-content 1fr;
  }

  .visual {
    background-image: var(--gradient-15);
    border-radius: 10px;
    block-size: 175px;
    overflow: clip;
  }

  .img {
    display: block;
    inline-size: 100%;
    block-size: 100%;
    object-fit: cover;
  }

  .figcaption {
    font-weight: var(--font-weight-7);
    font-size: var(--font-size-2);
    font-family: var(--font-family-default);
    & strong {
      color: #0680ff;
    }
  }
}

@layer layout {
  .chat {
    background-color: oklch(99% 0 287);
    block-size: 75svb;
    border-radius: var(--radius-3) var(--radius-3) 0px 0px;
    border-width: 0;
    box-shadow: var(--shadow-6);
    inline-size: min(1280px, 100%);
    inset-block-start: auto;
    margin-inline: auto;
    padding: 0;

    /* --opacityOK  */
    @media (prefers-reduced-transparency: no-preference) {
      backdrop-filter: blur(70px);
      background-color: oklch(99% 0 287/ 30%);
    }

    /* motionOK */
    @media (prefers-reduced-motion: no-preference) {
      animation: slide-out-down-dismiss 0.5s var(--ease-3);

      &:popover-open {
        /* Built-in animation from Open Props */
        animation: var(--animation-slide-in-up);
      }
    }
  }

  @keyframes slide-out-down-dismiss {
    from {
      display: block;
    }

    to {
      transform: translateY(100%);
    }
  }

  .chat-content-wrapper {
    display: grid;
    grid-template-rows: min-content 1fr;
    block-size: 100%;
  }

  .chat-header {
    border-bottom: var(--border-size-1) solid var(--gray-4);
    padding-block: 12px;
    padding-inline: 12px;
    display: grid;
    place-items: center end;
  }

  .chat-popover-close-btn {
    display: inline-grid;
    place-items: center;
    padding: 0;
    border-width: 0;
    block-size: 44px;
    inline-size: 44px;
    border-radius: var(--radius-round);
    cursor: pointer;
    background-color: var(--stone-2);
  }

  .chat-section {
    display: grid;
    grid-template-rows: 1fr min-content;
    padding-block: var(--size-5);
    padding-inline: var(--size-7);
  }

  .chat-input-container {
    display: grid;
    grid-template-columns: 1fr min-content;
    box-shadow: var(--shadow-4);
    border-radius: var(--radius-round);
    background-color: white;
    padding-inline: var(--size-px-4) var(--size-px-2);
    block-size: 58px;
    align-items: center;
  }

  .chat-textarea {
    font-size: var(--font-size-2);
    font-family: inherit;
    caret-color: blue;
    border-width: 0;
    background-color: transparent;
    outline-width: 0;
    outline: none;
    overflow: hidden;
    resize: none;
    block-size: var(--size-5);
  }

  .chat-submit-btn {
    color: blue;
    block-size: var(--size-px-8);
    inline-size: var(--size-px-8);
    border-width: 0;
    border-radius: var(--radius-round);
    background-color: transparent;
    cursor: pointer;
  }
}

@layer components {
  .btn {
    --_state-layer-op: 0%;
    --_state-layer-bg: oklch(1 0 0 / var(--_state-layer-op));
    background-color: transparent;
    font-weight: var(--font-weight-6);
    font-size: var(--font-size-1);
    font-family: var(--font-family-default);
    min-block-size: 40px;
    text-decoration: none;
    text-align: center;
    min-inline-size: max-content;
    display: inline-grid;
    place-items: center;
    border-radius: var(--radius-4);
    overflow: clip;
    isolation: isolate;
    -moz-user-select: none;
    -webkit-user-select: none;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    padding: 0;
    border-width: 0;

    &:hover {
      --_state-layer-op: 20%;
      box-shadow: var(--shadow-2);
    }

    &:is(:active, :disabled, :focus-within) {
      --_state-layer-op: 12%;
    }

    &.filled {
      background-color: var(--gray-12);
      color: white;
    }

    &.primary {
      --_state-layer-bg: oklch(0 0 0 / var(--_state-layer-op));
      background-color: var(--primary);
    }

    .state-layer {
      border-radius: inherit;
      padding-block: 12px;
      padding-inline: var(--size-3);
      inline-size: 100%;
      display: inline-grid;
      place-items: center;
      background-color: var(--_state-layer-bg);
      transition-property: background-color, box-shadow;
      transition-duration: 0.2s;
      transition-timing-function: var(--ease-3);
    }
  }

  .message {
    font-size: 0.7rem;
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    border-radius: 0.5rem;
    max-width: 96%;
  }

  .message.user {
    background: #0575e6;
    background: -webkit-linear-gradient(to right, #021b79, #0575e6);
    background: linear-gradient(to right, #021b79, #0575e6);

    color: $color-lighter;
    align-self: flex-end;
    @media screen and (min-width: 992px) {
      margin-right: 10px;
    }
  }

  .message.assistant {
    background: var(--card-bg);
    background-image: linear-gradient(60deg, #2b2828 0%, #292f35 100%);

    align-self: flex-start;
    color: #fff;
  }

  .chat-messages {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    height: 90%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .ia-loader-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .dot-loader {
    display: flex;
    gap: 0.4rem;
    justify-content: center;
    align-items: center;
    height: 1.5rem;
  }

  .dot-loader span {
    width: 0.5rem;
    height: 0.5rem;
    background-color: #0680ff;
    border-radius: 50%;
    animation: dot-blink 1.4s infinite ease-in-out both;
  }

  .dot-loader span:nth-child(1) {
    animation-delay: -0.32s;
  }
  .dot-loader span:nth-child(2) {
    animation-delay: -0.16s;
  }
  .dot-loader span:nth-child(3) {
    animation-delay: 0;
  }

  @keyframes dot-blink {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }

  .ia-loader-text {
    font-style: italic;
    color: #0680ff;
    font-size: 0.95rem;
    margin-top: 0.5rem;
  }
}
.triangle-down {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #6c757d; /* correspond à .text-muted Bootstrap */
}

.note-wrapper {
  display: inline-block;
  text-align: center;
  margin-top: -20px;
}
</style>
